import React, { useMemo, useState } from "react";
import { Button, Checkbox, DatePicker, Drawer, Empty, Form, Input, message, Modal, Select, Space, Table, Tag, TimePicker, Typography } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { useAircraftAvailabilityWindowList, useAircraftAvailabilityWindowMutations } from "../hooks/useAircraftAvailabilityWindowMutations";

dayjs.extend(utc);

const { RangePicker } = DatePicker;
const { Text } = Typography;
const DATE_TIME_MINUTE_FORMAT = "YYYY-MM-DD HH:mm";
const MINUTE_TIME_OPTIONS = { format: "HH:mm", showSecond: false };

const WINDOW_TYPE_OPTIONS = [
    { value: "SCHEDULED_MAINTENANCE", label: "计划维修" },
    { value: "UNSCHEDULED_MAINTENANCE", label: "非计划维修/AOG" },
    { value: "MODIFICATION", label: "改装/喷涂" },
    { value: "LEASE_RETURN", label: "租约归还" },
    { value: "RESERVATION", label: "预留/调机" },
    { value: "OTHER", label: "其他" },
];

const STATUS_OPTIONS = [
    { value: "ACTIVE", label: "生效" },
    { value: "COMPLETED", label: "已完成" },
    { value: "CANCELLED", label: "已取消" },
];

const WEEKDAY_OPTIONS = [
    { value: 1, label: "周一" },
    { value: 2, label: "周二" },
    { value: 3, label: "周三" },
    { value: 4, label: "周四" },
    { value: 5, label: "周五" },
    { value: 6, label: "周六" },
    { value: 7, label: "周日" },
];

const TIMEZONE_OPTIONS = [
    "UTC",
    "Asia/Shanghai",
    "Asia/Hong_Kong",
    "Asia/Singapore",
    "Asia/Tokyo",
    "Europe/Liege",
    "Europe/London",
    "America/Anchorage",
    "America/Los_Angeles",
].map((value) => ({ value, label: value }));

function getErrorMessage(error, fallback) {
    return error?.response?.data?.detail || error?.message || fallback;
}

function formatUtc(value) {
    return value ? dayjs.utc(value).format("YYYY-MM-DD HH:mm[Z]") : "持续至另行通知";
}

function toUtcClockIso(value) {
    if (!value) {
        return null;
    }
    return dayjs.utc(value.format("YYYY-MM-DDTHH:mm")).toISOString();
}

function timeValue(value) {
    return value ? value.format("HH:mm") : null;
}

function dateValue(value) {
    return value ? value.format("YYYY-MM-DD") : null;
}

function typeLabel(value) {
    return WINDOW_TYPE_OPTIONS.find((item) => item.value === value)?.label || value || "-";
}

function statusColor(status) {
    if (status === "ACTIVE") {
        return "green";
    }
    if (status === "CANCELLED") {
        return "default";
    }
    return "blue";
}

function normalizeAirportCode(value) {
    return value ? String(value).trim().toUpperCase() : null;
}

export function validateBatchTimezoneRequired(values) {
    return !normalizeAirportCode(values?.airport_code) && !values?.timezone ? "无机场时必须选择时区" : null;
}

export function getBatchPreviewCounts(batchPreview) {
    return {
        total: batchPreview?.total_count || 0,
        conflict: batchPreview?.conflict_count || 0,
        duplicate: batchPreview?.duplicate_count || 0,
        error: batchPreview?.error_count || 0,
    };
}

export function buildSinglePayload(values) {
    return {
        window_type: values.window_type,
        status: values.status || "ACTIVE",
        title: values.title || null,
        remarks: values.remarks || null,
        airport_code: normalizeAirportCode(values.airport_code),
        start_time: toUtcClockIso(values.start_time),
        end_time: values.open_ended ? null : toUtcClockIso(values.end_time),
    };
}

export function buildBatchPayload(values) {
    return {
        window_type: values.window_type,
        status: values.status || "ACTIVE",
        title: values.title || null,
        remarks: values.remarks || null,
        airport_code: normalizeAirportCode(values.airport_code),
        timezone: values.timezone || null,
        start_date: dateValue(values.date_range?.[0]),
        end_date: dateValue(values.date_range?.[1]),
        weekdays: values.weekdays || [],
        start_time: timeValue(values.start_time),
        end_time: values.duration_mode === "END_TIME" ? timeValue(values.end_time) : null,
        duration_minutes: values.duration_mode === "DURATION" ? Number(values.duration_minutes) : null,
    };
}

const AircraftAvailabilityWindowDrawer = ({ aircraft, open, onClose, canWrite = false, canDelete = false }) => {
    const aircraftId = aircraft?.id;
    const listQuery = useAircraftAvailabilityWindowList(aircraftId, { enabled: Boolean(open && aircraftId) });
    const mutations = useAircraftAvailabilityWindowMutations(aircraftId);
    const [form] = Form.useForm();
    const [batchForm] = Form.useForm();
    const [editingWindow, setEditingWindow] = useState(null);
    const [formOpen, setFormOpen] = useState(false);
    const [batchOpen, setBatchOpen] = useState(false);
    const [batchPreview, setBatchPreview] = useState(null);
    const [batchPayload, setBatchPayload] = useState(null);

    const windows = Array.isArray(listQuery.data) ? listQuery.data : [];
    const writeDisabled = !canWrite;

    const openCreateForm = () => {
        setEditingWindow(null);
        form.resetFields();
        form.setFieldsValue({ window_type: "SCHEDULED_MAINTENANCE", status: "ACTIVE", open_ended: false });
        setFormOpen(true);
    };

    const openEditForm = (record) => {
        setEditingWindow(record);
        form.setFieldsValue({
            window_type: record.window_type,
            status: record.status,
            title: record.title,
            remarks: record.remarks,
            airport_code: record.airport_code,
            start_time: record.start_time ? dayjs.utc(record.start_time).startOf("minute") : null,
            end_time: record.end_time ? dayjs.utc(record.end_time).startOf("minute") : null,
            open_ended: !record.end_time,
        });
        setFormOpen(true);
    };

    const submitSingle = async () => {
        try {
            const values = await form.validateFields();
            const payload = buildSinglePayload(values);
            if (!payload.end_time && !values.open_ended) {
                message.error("请填写结束时间，或勾选持续至另行通知");
                return;
            }
            if (editingWindow) {
                await mutations.updateWindow.mutateAsync({ windowId: editingWindow.id, payload });
                message.success("可用性窗口已更新");
            } else {
                await mutations.createWindow.mutateAsync(payload);
                message.success("可用性窗口已创建");
            }
            setFormOpen(false);
        } catch (error) {
            if (error?.errorFields) {
                return;
            }
            message.error(getErrorMessage(error, "保存可用性窗口失败"));
        }
    };

    const confirmDelete = (record) => {
        Modal.confirm({
            title: "确认删除可用性窗口",
            content: `${aircraft?.registration || "飞机"} · ${typeLabel(record.window_type)} · ${formatUtc(record.start_time)}`,
            okText: "删除",
            okType: "danger",
            cancelText: "取消",
            onOk: async () => {
                try {
                    await mutations.deleteWindow.mutateAsync(record.id);
                    message.success("可用性窗口已删除");
                } catch (error) {
                    message.error(getErrorMessage(error, "删除可用性窗口失败"));
                }
            },
        });
    };

    const openBatchForm = () => {
        batchForm.resetFields();
        batchForm.setFieldsValue({
            window_type: "SCHEDULED_MAINTENANCE",
            status: "ACTIVE",
            weekdays: [1, 2, 3, 4, 5],
            duration_mode: "DURATION",
            duration_minutes: 480,
        });
        setBatchPreview(null);
        setBatchPayload(null);
        setBatchOpen(true);
    };

    const previewBatch = async () => {
        try {
            const values = await batchForm.validateFields();
            const timezoneError = validateBatchTimezoneRequired(values);
            if (timezoneError) {
                batchForm.setFields([{ name: "timezone", errors: [timezoneError] }]);
                message.error(timezoneError);
                return;
            }
            const payload = buildBatchPayload(values);
            const response = await mutations.previewBatch.mutateAsync(payload);
            const preview = response?.data || response;
            setBatchPreview(preview);
            setBatchPayload(payload);
        } catch (error) {
            if (error?.errorFields) {
                return;
            }
            message.error(getErrorMessage(error, "批量预览失败"));
        }
    };

    const createBatch = async () => {
        try {
            const values = await batchForm.validateFields();
            const timezoneError = validateBatchTimezoneRequired(values);
            if (timezoneError) {
                batchForm.setFields([{ name: "timezone", errors: [timezoneError] }]);
                message.error(timezoneError);
                return;
            }
            const payload = batchPayload || buildBatchPayload(values);
            const response = await mutations.createBatch.mutateAsync(payload);
            const result = response?.data || response;
            message.success(`已创建 ${result?.created_count ?? 0} 个可用性窗口`);
            setBatchOpen(false);
        } catch (error) {
            if (error?.errorFields) {
                return;
            }
            message.error(getErrorMessage(error, "批量创建失败"));
        }
    };

    const columns = useMemo(() => [
        {
            title: "类型",
            dataIndex: "window_type",
            width: 140,
            render: (value) => typeLabel(value),
        },
        {
            title: "状态",
            dataIndex: "status",
            width: 90,
            render: (value) => <Tag color={statusColor(value)}>{STATUS_OPTIONS.find((item) => item.value === value)?.label || value}</Tag>,
        },
        {
            title: "开始 UTC",
            dataIndex: "start_time",
            width: 160,
            render: formatUtc,
        },
        {
            title: "结束 UTC",
            dataIndex: "end_time",
            width: 160,
            render: formatUtc,
        },
        {
            title: "机场",
            dataIndex: "airport_code",
            width: 80,
            render: (value) => value || "-",
        },
        {
            title: "原因/标题",
            dataIndex: "title",
            ellipsis: true,
            render: (value, record) => value || record.remarks || "-",
        },
        {
            title: "操作",
            key: "action",
            fixed: "right",
            width: 150,
            render: (_, record) => (
                <Space size="small">
                    <Button type="link" size="small" icon={<EditOutlined />} disabled={writeDisabled} onClick={() => openEditForm(record)}>编辑</Button>
                    <Button type="link" danger size="small" icon={<DeleteOutlined />} disabled={!canDelete} onClick={() => confirmDelete(record)}>删除</Button>
                </Space>
            ),
        },
    ], [canDelete, writeDisabled]);

    const previewColumns = [
        { title: "本地日期", dataIndex: "local_date", width: 120 },
        { title: "时区", dataIndex: "timezone", width: 150 },
        { title: "UTC 开始", dataIndex: "start_time_utc", width: 170, render: formatUtc },
        { title: "UTC 结束", dataIndex: "end_time_utc", width: 170, render: formatUtc },
        { title: "重复", dataIndex: "duplicate", width: 80, render: (value) => (value ? <Tag color="gold">重复</Tag> : "-") },
        { title: "冲突", dataIndex: "conflicts", width: 90, render: (value) => (value?.length ? <Tag color="orange">{value.length}</Tag> : "-") },
    ];

    return (
        <Drawer
            width={980}
            title={`可用性/维修窗口 · ${aircraft?.registration || ""}`}
            open={open}
            onClose={onClose}
            destroyOnClose
            extra={(
                <Space>
                    <Button icon={<ReloadOutlined />} onClick={() => listQuery.refetch()} loading={listQuery.isFetching}>刷新</Button>
                    <Button onClick={openBatchForm} disabled={writeDisabled}>批量新增</Button>
                    <Button type="primary" icon={<PlusOutlined />} onClick={openCreateForm} disabled={writeDisabled}>新增窗口</Button>
                </Space>
            )}
        >
            {writeDisabled ? <Text type="secondary">{canDelete ? "当前账号没有新增/编辑权限，可删除已有窗口。" : "当前账号没有写入权限，仅可查看窗口。"}</Text> : null}
            <Table
                style={{ marginTop: 12 }}
                size="small"
                rowKey="id"
                columns={columns}
                dataSource={windows}
                loading={listQuery.isLoading || listQuery.isFetching}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无维修/不可用窗口" /> }}
                pagination={{ pageSize: 10 }}
                scroll={{ x: 980 }}
            />

            <Modal
                title={editingWindow ? "编辑可用性窗口" : "新增可用性窗口"}
                open={formOpen}
                onCancel={() => setFormOpen(false)}
                onOk={submitSingle}
                okButtonProps={{ loading: mutations.createWindow.isLoading || mutations.updateWindow.isLoading }}
                destroyOnHidden
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="window_type" label="窗口类型" rules={[{ required: true, message: "请选择窗口类型" }]}>
                        <Select options={WINDOW_TYPE_OPTIONS} />
                    </Form.Item>
                    <Form.Item name="status" label="状态" rules={[{ required: true, message: "请选择状态" }]}>
                        <Select options={STATUS_OPTIONS} />
                    </Form.Item>
                    <Form.Item name="title" label="原因/标题">
                        <Input placeholder="例如 C检、AOG、租约归还" />
                    </Form.Item>
                    <Form.Item name="airport_code" label="关联机场">
                        <Input placeholder="例如 HKG，可留空" maxLength={10} />
                    </Form.Item>
                    <Form.Item name="start_time" label="开始时间 UTC" rules={[{ required: true, message: "请选择开始时间" }]}>
                        <DatePicker showTime={MINUTE_TIME_OPTIONS} format={DATE_TIME_MINUTE_FORMAT} style={{ width: "100%" }} />
                    </Form.Item>
                    <Form.Item name="open_ended" valuePropName="checked">
                        <Checkbox>持续至另行通知</Checkbox>
                    </Form.Item>
                    <Form.Item shouldUpdate noStyle>
                        {({ getFieldValue }) => (
                            <Form.Item name="end_time" label="结束时间 UTC" rules={getFieldValue("open_ended") ? [] : [{ required: true, message: "请选择结束时间" }]}>
                                <DatePicker showTime={MINUTE_TIME_OPTIONS} format={DATE_TIME_MINUTE_FORMAT} style={{ width: "100%" }} disabled={getFieldValue("open_ended")} />
                            </Form.Item>
                        )}
                    </Form.Item>
                    <Form.Item name="remarks" label="备注/原因说明">
                        <Input.TextArea rows={3} />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                width={920}
                title="批量新增可用性窗口"
                open={batchOpen}
                onCancel={() => setBatchOpen(false)}
                footer={[
                    <Button key="cancel" onClick={() => setBatchOpen(false)}>取消</Button>,
                    <Button key="preview" onClick={previewBatch} loading={mutations.previewBatch.isLoading}>预览展开</Button>,
                    <Button key="create" type="primary" onClick={createBatch} loading={mutations.createBatch.isLoading} disabled={!batchPreview || batchPreview.error_count > 0}>创建预览窗口</Button>,
                ]}
                destroyOnHidden
            >
                <Form form={batchForm} layout="vertical" onValuesChange={() => { setBatchPreview(null); setBatchPayload(null); }}>
                    <Space align="start" wrap>
                        <Form.Item name="window_type" label="窗口类型" rules={[{ required: true }]} style={{ width: 220 }}>
                            <Select options={WINDOW_TYPE_OPTIONS} />
                        </Form.Item>
                        <Form.Item name="status" label="状态" rules={[{ required: true }]} style={{ width: 140 }}>
                            <Select options={STATUS_OPTIONS} />
                        </Form.Item>
                        <Form.Item name="airport_code" label="机场" style={{ width: 140 }}>
                            <Input placeholder="可选" maxLength={10} />
                        </Form.Item>
                        <Form.Item
                            name="timezone"
                            label="时区"
                            tooltip="有机场且留空时后端使用机场 timezone；无机场时必须显式选择，包括 UTC。"
                            dependencies={["airport_code"]}
                            rules={[
                                ({ getFieldValue }) => ({
                                    validator: (_, value) => {
                                        const error = validateBatchTimezoneRequired({ airport_code: getFieldValue("airport_code"), timezone: value });
                                        return error ? Promise.reject(new Error(error)) : Promise.resolve();
                                    },
                                }),
                            ]}
                            style={{ width: 220 }}
                        >
                            <Select showSearch allowClear options={TIMEZONE_OPTIONS} placeholder="无机场时必选" />
                        </Form.Item>
                    </Space>
                    <Form.Item name="date_range" label="日期范围" rules={[{ required: true, message: "请选择日期范围" }]}>
                        <RangePicker />
                    </Form.Item>
                    <Form.Item name="weekdays" label="星期" rules={[{ required: true, message: "请选择星期" }]}>
                        <Checkbox.Group options={WEEKDAY_OPTIONS} />
                    </Form.Item>
                    <Space align="start" wrap>
                        <Form.Item name="start_time" label="本地开始时刻" rules={[{ required: true }]}>
                            <TimePicker format="HH:mm" showSecond={false} />
                        </Form.Item>
                        <Form.Item name="duration_mode" label="结束方式" rules={[{ required: true }]}>
                            <Select style={{ width: 160 }} options={[{ value: "DURATION", label: "持续时长" }, { value: "END_TIME", label: "结束时刻" }]} />
                        </Form.Item>
                        <Form.Item shouldUpdate noStyle>
                            {({ getFieldValue }) => (getFieldValue("duration_mode") === "END_TIME" ? (
                                <Form.Item name="end_time" label="本地结束时刻" rules={[{ required: true }]}>
                                    <TimePicker format="HH:mm" showSecond={false} />
                                </Form.Item>
                            ) : (
                                <Form.Item name="duration_minutes" label="持续分钟数" rules={[{ required: true }]}>
                                    <Input type="number" min={1} style={{ width: 140 }} />
                                </Form.Item>
                            ))}
                        </Form.Item>
                    </Space>
                    <Form.Item name="title" label="原因/标题">
                        <Input placeholder="批量窗口共享标题" />
                    </Form.Item>
                    <Form.Item name="remarks" label="备注">
                        <Input.TextArea rows={2} />
                    </Form.Item>
                </Form>
                {batchPreview ? (
                    <div style={{ marginTop: 12 }}>
                        <Space style={{ marginBottom: 8 }}>
                            <Tag color="blue">展开 {getBatchPreviewCounts(batchPreview).total}</Tag>
                            <Tag color={getBatchPreviewCounts(batchPreview).conflict ? "orange" : "default"}>冲突 {getBatchPreviewCounts(batchPreview).conflict}</Tag>
                            <Tag color={getBatchPreviewCounts(batchPreview).duplicate ? "gold" : "default"}>重复 {getBatchPreviewCounts(batchPreview).duplicate}</Tag>
                            <Tag color={getBatchPreviewCounts(batchPreview).error ? "red" : "default"}>错误 {getBatchPreviewCounts(batchPreview).error}</Tag>
                        </Space>
                        <Table size="small" rowKey={(row) => `${row.local_date}:${row.start_time_utc}`} columns={previewColumns} dataSource={batchPreview.items || []} pagination={{ pageSize: 5 }} scroll={{ x: 860 }} />
                    </div>
                ) : null}
            </Modal>
        </Drawer>
    );
};

export default AircraftAvailabilityWindowDrawer;
